<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { getProduct, type Product } from '../api';
import { locale, text } from '../i18n';
import { applyDocumentSeo } from '../seo';

const route = useRoute();
const product = ref<Product | null>(null);
const loading = ref(true);
const error = ref('');
const activeImage = ref(0);
const activeMedia = computed(() => product.value?.media[activeImage.value] ?? null);
const groupedSpecs = computed(() => {
  const groups = new Map<string, Product['specs']>();
  for (const item of product.value?.specs ?? []) {
    const name = item.groupName || text('技术参数', 'Specifications');
    groups.set(name, [...(groups.get(name) ?? []), item]);
  }
  return [...groups.entries()];
});

watch([() => route.params.slug, locale], async ([slug]) => {
  loading.value = true;
  error.value = '';
  activeImage.value = 0;
  try {
    product.value = await getProduct(locale.value, String(slug));
    applyDocumentSeo(product.value.seo.title, product.value.seo.description);
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : text('产品加载失败', 'Unable to load product');
    product.value = null;
  } finally {
    loading.value = false;
  }
}, { immediate: true });
</script>

<template>
  <section class="section product-detail-section">
    <div
      v-if="product"
      class="container"
    >
      <nav
        class="breadcrumb"
        aria-label="Breadcrumb"
      >
        <RouterLink to="/">
          {{ text('首页', 'Home') }}
        </RouterLink><span>/</span><RouterLink to="/products">
          {{ text('产品中心', 'Products') }}
        </RouterLink><span>/</span><b>{{ product.name }}</b>
      </nav>
      <div class="product-overview">
        <div class="product-gallery">
          <div class="product-gallery__main">
            <img
              v-if="activeMedia"
              :src="activeMedia.url"
              :alt="activeMedia.alt"
            >
            <div
              v-else
              class="media-placeholder"
            >
              JWS
            </div>
          </div>
          <div
            v-if="product.media.length > 1"
            class="product-gallery__thumbs"
          >
            <button
              v-for="(image, index) in product.media"
              :key="image.id"
              type="button"
              :class="{ active: index === activeImage }"
              @click="activeImage = index"
            >
              <img
                :src="image.url"
                :alt="image.alt"
              >
            </button>
          </div>
        </div>
        <div class="product-summary">
          <span class="eyebrow">{{ product.category.name }}</span>
          <h1>{{ product.name }}</h1>
          <p>{{ product.summary }}</p>
          <dl>
            <div v-if="product.sku">
              <dt>{{ text('产品型号', 'Model') }}</dt><dd>{{ product.sku }}</dd>
            </div>
            <div><dt>{{ text('业务类型', 'Business type') }}</dt><dd>{{ text('设备与项目方案', 'Equipment and project solution') }}</dd></div>
            <div><dt>{{ text('交付支持', 'Delivery support') }}</dt><dd>{{ text('方案、部署、培训与售后', 'Planning, deployment, training and support') }}</dd></div>
          </dl>
          <div class="product-summary__actions">
            <RouterLink
              class="button"
              :to="{ path: '/contact', query: { product: product.id, name: product.name } }"
            >
              {{ text('获取报价与方案', 'Request pricing and a solution') }}
            </RouterLink><RouterLink
              class="text-link"
              to="/contact"
            >
              {{ text('联系技术团队', 'Talk to our technical team') }} <b>→</b>
            </RouterLink>
          </div>
        </div>
      </div>

      <div class="product-content-layout">
        <article class="rich-content">
          <span class="eyebrow">{{ text('产品说明', 'Product overview') }}</span>
          <h2>{{ text('能力、特点与适用场景', 'Capabilities, features and applications') }}</h2>
          <div
            v-if="product.descriptionHtml"
            v-html="product.descriptionHtml"
          />
          <p v-else>
            {{ product.summary }}
          </p>
          <div
            v-if="product.features.length"
            class="feature-list"
          >
            <h3>{{ text('主要特点', 'Key features') }}</h3><ul>
              <li
                v-for="item in product.features"
                :key="item"
              >
                {{ item }}
              </li>
            </ul>
          </div>
        </article>
        <aside class="inquiry-panel">
          <span>{{ text('项目选型', 'Project selection') }}</span><h2>{{ text('需要确认现场是否适配？', 'Need to confirm site compatibility?') }}</h2><p>{{ text('提供通道尺寸、检查对象、通行量和联网要求，我们将协同技术团队给出配置建议。', 'Share lane dimensions, screening targets, throughput and integration requirements for a configuration recommendation.') }}</p><RouterLink
            class="button"
            :to="{ path: '/contact', query: { product: product.id, name: product.name } }"
          >
            {{ text('提交项目需求', 'Submit project requirements') }}
          </RouterLink>
        </aside>
      </div>

      <section
        v-if="groupedSpecs.length"
        class="spec-section"
      >
        <span class="eyebrow">{{ text('规格资料', 'Specifications') }}</span><h2>{{ text('产品参数与供货信息', 'Product and supply information') }}</h2>
        <div
          v-for="[group, items] in groupedSpecs"
          :key="group"
          class="spec-group"
        >
          <h3>{{ group }}</h3><dl>
            <div
              v-for="item in items"
              :key="`${item.label}-${item.value}`"
            >
              <dt>{{ item.label }}</dt><dd>{{ item.value }}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section
        v-if="product.attributes.brand || product.attributes.certification || product.attributes.origin || product.attributes.custom.length || product.trade.moq || product.trade.price || product.trade.paymentMethod || product.trade.supplyAbility || product.trade.deliveryTime || product.trade.packaging"
        class="spec-section product-attributes-section"
      >
        <span class="eyebrow">{{ text('产品资料', 'Product data') }}</span><h2>{{ text('属性与交易信息', 'Attributes and trade information') }}</h2>
        <dl class="product-detail-data-list">
          <div v-if="product.attributes.brand"><dt>{{ text('品牌', 'Brand') }}</dt><dd>{{ product.attributes.brand }}</dd></div>
          <div v-if="product.attributes.certification"><dt>{{ text('认证证书', 'Certification') }}</dt><dd>{{ product.attributes.certification }}</dd></div>
          <div v-if="product.attributes.origin"><dt>{{ text('原产地', 'Origin') }}</dt><dd>{{ product.attributes.origin }}</dd></div>
          <div v-if="product.trade.moq"><dt>{{ text('最小起订量', 'Minimum order quantity') }}</dt><dd>{{ product.trade.moq }}</dd></div>
          <div v-if="product.trade.price"><dt>{{ text('价格', 'Price') }}</dt><dd>{{ product.trade.price }}</dd></div>
          <div v-if="product.trade.paymentMethod"><dt>{{ text('付款方式', 'Payment method') }}</dt><dd>{{ product.trade.paymentMethod }}</dd></div>
          <div v-if="product.trade.supplyAbility"><dt>{{ text('供货能力', 'Supply ability') }}</dt><dd>{{ product.trade.supplyAbility }}</dd></div>
          <div v-if="product.trade.deliveryTime"><dt>{{ text('发货期限', 'Delivery time') }}</dt><dd>{{ product.trade.deliveryTime }}</dd></div>
          <div v-if="product.trade.packaging"><dt>{{ text('常规包装', 'Packaging') }}</dt><dd>{{ product.trade.packaging }}</dd></div>
          <template v-for="attribute in product.attributes.custom" :key="`${attribute.name}-${attribute.sortOrder}`"><div><dt>{{ attribute.name }}</dt><dd>{{ attribute.value }}</dd></div></template>
        </dl>
      </section>

      <section
        v-if="product.attachments.length"
        class="spec-section product-attachments-section"
      >
        <span class="eyebrow">{{ text('资料下载', 'Downloads') }}</span><h2>{{ text('产品图片与附件', 'Product files') }}</h2>
        <div class="product-attachments-list">
          <a
            v-for="attachment in product.attachments"
            :key="`${attachment.title}-${attachment.url}`"
            class="product-attachment-link"
            :href="attachment.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{{ attachment.title }}</span><b>↓</b>
          </a>
        </div>
      </section>

      <section v-if="product.videos.length" class="spec-section product-videos-section">
        <span class="eyebrow">{{ text('产品视频', 'Product videos') }}</span><h2>{{ text('操作演示与应用场景', 'Demonstrations and applications') }}</h2>
        <div class="product-videos-grid"><article v-for="video in product.videos" :key="video.id" class="product-video-card"><video v-if="video.playbackUrl" controls preload="metadata" :poster="video.posterUrl || undefined"><source :src="video.playbackUrl" type="video/mp4"></video><h3>{{ video.title }}</h3><p v-if="video.description">{{ video.description }}</p></article></div>
      </section>
    </div>
    <div
      v-else
      class="container empty-state empty-state--large"
    >
      {{ loading ? text('正在加载产品…', 'Loading product…') : error }}
    </div>
  </section>
</template>
